// Schedule-send API client. Backed by /api/mail/schedule (multipart create) +
// /api/mail/scheduled (list/cancel/reschedule). Cookie-authed like the rest.
//
// The create call is multipart/form-data (raw bytes ride along as `attachments`
// file parts), so it bypasses the JSON `http` helper and does a raw fetch with
// the same error handling as JmapApi.uploadBlob.

import { http } from "./http";

export interface ScheduledEmail {
  id: string;
  from: string;
  to: { email: string; name?: string }[];
  cc: { email: string; name?: string }[];
  subject: string;
  preview: string;
  in_reply_to?: string;
  attachments: { name: string; type: string; size: number }[];
  fire_at: number;
  status: "pending" | "sending" | "sent" | "failed" | "cancelled";
  send_error?: string;
  created_at: number;
  sent_at?: number;
}

// JSON payload appended under the multipart `payload` field. fire_at is ms epoch.
export interface SchedulePayload {
  from?: string;
  to: { email: string; name?: string }[];
  cc?: { email: string; name?: string }[];
  bcc?: { email: string; name?: string }[];
  subject: string;
  text?: string;
  html?: string;
  in_reply_to?: string;
  references?: string[];
  fire_at: number;
}

export const MailScheduleApi = {
  // Multipart create. Build FormData by hand — DON'T set Content-Type (the
  // browser sets the multipart boundary) and DON'T route through the JSON http
  // helper. Mirrors JmapApi.uploadBlob's raw-fetch error handling.
  async schedule(payload: SchedulePayload, files: File[]): Promise<{ id: string; fire_at: number }> {
    const form = new FormData();
    form.append("payload", JSON.stringify(payload));
    for (const f of files) form.append("attachments", f, f.name);
    const res = await fetch("/api/mail/schedule", {
      method: "POST",
      body: form,
      credentials: "same-origin",
      headers: { "Origin": window.location.origin },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`/api/mail/schedule ${res.status}: ${text.slice(0, 200)}`);
    }
    return res.json();
  },

  list(): Promise<ScheduledEmail[]> {
    return http<{ items: ScheduledEmail[] }>("/api/mail/scheduled").then((r) => r.items);
  },

  cancel(id: string): Promise<void> {
    return http(`/api/mail/scheduled/${encodeURIComponent(id)}/cancel`, { method: "POST" });
  },

  reschedule(id: string, fireAt: number): Promise<void> {
    return http(`/api/mail/scheduled/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ fire_at: fireAt }),
    });
  },
};
